import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import { viewAllAdmins, updateAdminStatus, switchToAdminDashboard, exitAdminView, updateDgcaCompany, setAdminKeyLimit } from "../Apis/SuperadminApis";
import { useNavigate } from "react-router-dom";
import "./ViewAllAdmins.css";
import pavamanLogo from "../images/drone.png";
import PopupMessage from "../PopupMessage/PopupMessage";
import { FiSearch, FiFilter } from "react-icons/fi";

const ViewAllAdmins = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [approvedAdmins, setApprovedAdmins] = useState([]);
  const [rejectedAdmins, setRejectedAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState("pending-invitations");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("info");
  const [switchLoading, setSwitchLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [dgcaLoadingId, setDgcaLoadingId] = useState(null);
  const [keyLimitLoadingId, setKeyLimitLoadingId] = useState(null);
  const [keyLimitInputs, setKeyLimitInputs] = useState({});
  const [showKeyLimitInput, setShowKeyLimitInput] = useState({});

  useEffect(() => {
    loadAllAdmins();
  }, []);

  const loadAllAdmins = async () => {
    try {
      setIsLoading(true);

      const response = await viewAllAdmins({});

      if (response?.error) {
        if (
          response?.error &&
          response.error.toLowerCase().includes("unauthorized")
        ) {
          localStorage.clear();
          sessionStorage.clear();

          setPopupMessage("Session expired, redirecting to login page");
          setPopupType("error");
          setPopupOpen(true);

          setTimeout(() => {
            navigate("/login");
          }, 2000);

          return;
        }

        // Other API errors
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
        return;
      }

      if (response.data) {
        setAdmins(response.data);
        categorizeAdmins(response.data);
      } else {
        setAdmins([]);
        categorizeAdmins([]);
      }
    } catch (error) {
      console.error('Failed to load admins:', error);

      if (
        error?.response?.status === 401 ||
        (
          error?.response?.data?.error &&
          error.response.data.error.toLowerCase().includes("unauthorized")
        )
      ) {
        setPopupMessage("Session expired, redirecting to login page");
        setPopupType("error");
        setPopupOpen(true);

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        return;
      }

      setPopupMessage('Failed to load admins. Please try again.');
      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const categorizeAdmins = (adminsList) => {
    const invitations = adminsList.filter(admin =>
      admin.approval === 0 && !admin.name
    );

    const registrations = adminsList.filter(admin =>
      admin.approval === 1
    );

    const approved = adminsList.filter(admin =>
      admin.approval === 2
    );

    const rejected = adminsList.filter(admin =>
      admin.approval === 3
    );

    setPendingInvitations(invitations);
    setPendingRegistrations(registrations);
    setApprovedAdmins(approved);
    setRejectedAdmins(rejected);
  };

  const handleUpdateStatus = async (admin, action) => {
    try {
      setActionLoading(true);

      const response = await updateAdminStatus({
        email: admin.email,
        action: action
      });

      if (response?.error) {
        // Session expired
        if (
          response?.error &&
          response.error.toLowerCase().includes("unauthorized")
        ) {
          localStorage.clear();
          sessionStorage.clear();

          setPopupMessage("Session expired, redirecting to login page");
          setPopupType("error");
          setPopupOpen(true);

          setTimeout(() => {
            navigate("/login");
          }, 2000);

          return;
        }

        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
        setTimeout(() => {
          setPopupOpen(false);
        }, 3000);
      } else if (response.message) {
        setPopupMessage(response.message);
        setPopupType("success");
        setPopupOpen(true);
        await loadAllAdmins();
        setTimeout(() => {
          setPopupOpen(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to update admin status:', error);

      if (
        error?.response?.status === 401 ||
        (
          error?.response?.data?.error &&
          error.response.data.error.toLowerCase().includes("unauthorized")
        )
      ) {
        setPopupMessage("Session expired, redirecting to login page");
        setPopupType("error");
        setPopupOpen(true);

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        return;
      }

      setPopupMessage('Failed to update admin status. Please try again.');
      setPopupType("error");
      setPopupOpen(true);
      setTimeout(() => {
        setPopupOpen(false);
      }, 3000);
    } finally {
      setActionLoading(false);
      setShowActionModal(false);
      setActionType(null);
      setSelectedAdmin(null);
    }
  };

const handleSwitchToAdmin = async (admin) => {
  try {
    setSwitchLoading(true);

    const response = await switchToAdminDashboard({
      admin_id: admin.id,
    });

    if (response?.error) {
      // Session expired
      if (
        response.error.toLowerCase().includes("unauthorized")
      ) {
        localStorage.clear();
        sessionStorage.clear();

        setPopupMessage("Session expired, redirecting to login page");
        setPopupType("error");
        setPopupOpen(true);

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        return;
      }

      // Other API errors
      setPopupMessage(response.error);
      setPopupType("error");
      setPopupOpen(true);

      setTimeout(() => {
        setPopupOpen(false);
      }, 3000);

      return;
    }

    if (response?.message) {
      // Use the selected Admin's DGCA status
      // Superadmin's own DGCA status should not be carried into Admin view
      const dgcaStatus = admin.is_dgca_company === true;
const superadminCompanyStatus =
  admin.is_superadmin_company === true;
      // Store switched Admin details
      localStorage.setItem(
        "view_as_admin_id",
        response.view_as_admin_id
      );
      localStorage.setItem(
        "view_as_admin_name",
        admin.name || admin.email
      );
      localStorage.setItem(
        "view_as_admin_logo",
        admin.logo_url || ""
      );
      localStorage.setItem(
        "is_dgca_company",
        String(dgcaStatus)
      );
      localStorage.setItem(
  "is_superadmin_company",
  String(superadminCompanyStatus)
);

      sessionStorage.setItem(
        "view_as_admin_id",
        response.view_as_admin_id
      );
      sessionStorage.setItem(
        "view_as_admin_name",
        admin.name || admin.email
      );
      sessionStorage.setItem(
        "view_as_admin_logo",
        admin.logo_url || ""
      );
      sessionStorage.setItem(
        "is_dgca_company",
        String(dgcaStatus)
      );
      sessionStorage.setItem(
  "is_superadmin_company",
  String(superadminCompanyStatus)
)

      // Success popup
      setPopupMessage(response.message);
      setPopupType("success");
      setPopupOpen(true);

      setTimeout(() => {
        setPopupOpen(false);
        window.location.href = "/admin/dashboard";
      }, 1500);
    }
  } catch (error) {
    console.error("Failed to switch to admin:", error);

    // Session expired / unauthorized
    if (
      error?.response?.status === 401 ||
      (
        error?.response?.data?.error &&
        error.response.data.error
          .toLowerCase()
          .includes("unauthorized")
      )
    ) {
      setPopupMessage("Session expired, redirecting to login page");
      setPopupType("error");
      setPopupOpen(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);

      return;
    }

    // Generic error
    setPopupMessage(
      "Failed to switch to admin dashboard. Please try again."
    );
    setPopupType("error");
    setPopupOpen(true);

    setTimeout(() => {
      setPopupOpen(false);
    }, 3000);
  } finally {
    setSwitchLoading(false);
  }
};

  const handleDgcaStatusChange = async (admin) => {
    try {
      setDgcaLoadingId(admin.id);

      const newStatus = !admin.is_dgca_company;

      const response = await updateDgcaCompany({
        admin_id: admin.id,
        is_dgca_company: newStatus
      });

      if (response?.error) {
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
        return;
      }

      setPopupMessage(
        newStatus
          ? `${admin.name || "Company"} marked as DGCA company.`
          : `${admin.name || "Company"} removed from DGCA company.`
      );

      setPopupType("success");
      setPopupOpen(true);

      // Show key-limit input only when DGCA is newly enabled
      if (newStatus) {
        setShowKeyLimitInput(prev => ({
          ...prev,
          [admin.id]: true
        }));
      } else {
        setShowKeyLimitInput(prev => ({
          ...prev,
          [admin.id]: false
        }));

        setKeyLimitInputs(prev => ({
          ...prev,
          [admin.id]: ""
        }));
      }

      await loadAllAdmins();

      setTimeout(() => {
        setPopupOpen(false);
      }, 3000);

    } catch (error) {
      console.error(
        "Failed to update DGCA company status:",
        error
      );

      setPopupMessage(
        error?.response?.data?.error ||
        "Failed to update DGCA company status."
      );

      setPopupType("error");
      setPopupOpen(true);

    } finally {
      setDgcaLoadingId(null);
    }
  };

  const handleSuperadminCompanyStatusChange = async (admin) => {
    try {
      setDgcaLoadingId(admin.id);

      const newStatus = !admin.is_superadmin_company;

      const response = await updateDgcaCompany({
        admin_id: admin.id,
        is_superadmin_company: newStatus
      });

      if (response?.error) {
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
        return;
      }

      setPopupMessage(
        newStatus
          ? `${admin.name || "Company"} marked as Superadmin company.`
          : `${admin.name || "Company"} removed from Superadmin company.`
      );

      setPopupType("success");
      setPopupOpen(true);

      await loadAllAdmins();

      setTimeout(() => {
        setPopupOpen(false);
      }, 3000);

    } catch (error) {
      console.error(
        "Failed to update superadmin company status:",
        error
      );

      setPopupMessage(
        error?.response?.data?.error ||
        "Failed to update superadmin company status."
      );

      setPopupType("error");
      setPopupOpen(true);

    } finally {
      setDgcaLoadingId(null);
    }
  };

  const handleKeyLimitUpdate = async (admin) => {
    try {
      const inputValue = keyLimitInputs[admin.id];

      if (
        inputValue === "" ||
        inputValue === null ||
        inputValue === undefined
      ) {
        setPopupMessage("Please enter a key limit.");
        setPopupType("error");
        setPopupOpen(true);
        return;
      }

      const keyLimit = Number(inputValue);

      if (!Number.isInteger(keyLimit) || keyLimit < 0) {
        setPopupMessage(
          "Key limit must be a valid non-negative number."
        );
        setPopupType("error");
        setPopupOpen(true);
        return;
      }

      setKeyLimitLoadingId(admin.id);

      const response = await setAdminKeyLimit({
        admin_id: admin.id,
        key_limit: keyLimit
      });

      if (response?.error) {
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
        return;
      }

      setPopupMessage(
        response.message || "Key limit updated successfully."
      );
      setPopupType("success");
      setPopupOpen(true);
      // Clear the input value
      setKeyLimitInputs(prev => ({
        ...prev,
        [admin.id]: ""
      }));

      // Close the key-limit edit section after successful save
      setShowKeyLimitInput(prev => ({
        ...prev,
        [admin.id]: false
      }));

      await loadAllAdmins();

      setTimeout(() => {
        setPopupOpen(false);
      }, 3000);

    } catch (error) {
      console.error("Failed to update key limit:", error);

      setPopupMessage(
        error?.response?.data?.error ||
        "Failed to update key limit."
      );

      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setKeyLimitLoadingId(null);
    }
  };

  const handleEditKeyLimit = (admin) => {
    setKeyLimitInputs(prev => ({
      ...prev,
      [admin.id]: admin.key_limit ?? ""
    }));

    setShowKeyLimitInput(prev => ({
      ...prev,
      [admin.id]: true
    }));
  };

  const handleExitAdminView = async () => {
    try {
      setSwitchLoading(true);
      const response = await exitAdminView();

      if (response?.error) {
        // Session expired
        if (
          response?.error &&
          response.error.toLowerCase().includes("unauthorized")
        ) {
          localStorage.clear();
          sessionStorage.clear();

          setPopupMessage("Session expired, redirecting to login page");
          setPopupType("error");
          setPopupOpen(true);

          setTimeout(() => {
            navigate("/login");
          }, 2000);

          return;
        }

        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
      } else if (response.message) {
        localStorage.removeItem('view_as_admin_id');
        localStorage.removeItem('view_as_admin_name');
        localStorage.removeItem('view_as_admin_logo');

        sessionStorage.removeItem('view_as_admin_id');
        sessionStorage.removeItem('view_as_admin_name');
        sessionStorage.removeItem('view_as_admin_logo');

        setPopupMessage(response.message);
        setPopupType("success");
        setPopupOpen(true);

        setTimeout(() => {
          setPopupOpen(false);
          navigate('/superadmin/dashboard');
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to exit admin view:', error);

      if (
        error?.response?.status === 401 ||
        (
          error?.response?.data?.error &&
          error.response.data.error.toLowerCase().includes("unauthorized")
        )
      ) {
        setPopupMessage("Session expired, redirecting to login page");
        setPopupType("error");
        setPopupOpen(true);

        setTimeout(() => {
          navigate("/login");
        }, 2000);

        return;
      }

      setPopupMessage('Failed to exit admin view. Please try again.');
      setPopupType("error");
      setPopupOpen(true);
      setTimeout(() => {
        setPopupOpen(false);
      }, 3000);
    } finally {
      setSwitchLoading(false);
    }
  };

  const openActionModal = (admin, type) => {
    setSelectedAdmin(admin);
    setActionType(type);
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    if (actionLoading) return;

    setShowActionModal(false);
    setActionType(null);
    setSelectedAdmin(null);
  };

  const handleConfirmedAction = async () => {
    if (!selectedAdmin || !actionType) return;

    const admin = selectedAdmin;
    const action = actionType;

    setShowActionModal(false);

    switch (action) {
      case "approve":
      case "reject":
      case "activate":
        await handleUpdateStatus(admin, action);
        break;

      case "dgca":
        await handleDgcaStatusChange(admin);
        break;

      case "superadmin":
        await handleSuperadminCompanyStatusChange(admin);
        break;

      case "switch":
        await handleSwitchToAdmin(admin);
        break;

      default:
        break;
    }

    setActionType(null);
    setSelectedAdmin(null);
  };


  const getApprovalStatus = (approval) => {
    const approvalValue = Number(approval);
    switch (approvalValue) {
      case 0:
        return { text: 'Pending', color: '#f39c12', bg: '#fef3c7', icon: '⏳', canApprove: false, canReject: false, canSuspend: false, canActivate: false };
      case 1:
        return { text: 'Pending Approval', color: '#FFA500', bg: '#FFF3CD', icon: '📝', canApprove: true, canReject: true, canSuspend: false, canActivate: false };
      case 2:
        return { text: 'Active', color: '#00B894', bg: '#D4EDDA', icon: '✓', canApprove: false, canReject: true, canSuspend: false, canActivate: false }; // CHANGE: canReject: true
      case 3:
        return { text: 'Rejected', color: '#FF4757', bg: '#F8D7DA', icon: '✗', canApprove: false, canReject: false, canSuspend: false, canActivate: true }; // CHANGE: text to 'Rejected'
      default:
        return { text: 'Unknown', color: '#95a5a6', bg: '#f3f4f6', icon: '?', canApprove: false, canReject: false, canSuspend: false, canActivate: false };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleViewDetails = (admin) => {
    setSelectedAdmin(admin);
    setShowDetailsModal(true);
  };

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedAdmin(null);
  };

  const getFilteredData = () => {
    let data = [];
    switch (filter) {
      case "pending-invitations":
        data = pendingInvitations;
        break;
      case "pending-registrations":
        data = pendingRegistrations;
        break;
      case "approved":
        data = approvedAdmins;
        break;
      case "rejected":
        data = rejectedAdmins;
        break;
      default:
        data = pendingInvitations;
    }

    if (searchTerm) {
      data = data.filter(admin =>
        (admin.email && admin.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.name && admin.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.contact_name && admin.contact_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (admin.mobile_no && admin.mobile_no.includes(searchTerm))
      );
    }

    return data;
  };

  const filteredData = getFilteredData();

  const statusCounts = {
    "pending-invitations": pendingInvitations.length,
    "pending-registrations": pendingRegistrations.length,
    "approved": approvedAdmins.length,
    "rejected": rejectedAdmins.length,
  };

  const renderInvitationCard = (admin) => (
    <div key={admin.id} className="va-admin-card va-card-invitation">
      <div className="va-card-header">
        <div className="va-avatar" style={{ background: "#f39c12" }}>
          📧
          <div className="va-pending-badge-avatar">1</div>
        </div>
        <div className="va-card-info">
          <div className="va-card-name-row">
            <h3 className="va-card-name">Invitation Sent</h3>
            <span className="va-status-badge" style={{ background: "#fef3c7", color: "#f39c12" }}>
              ⏳ Pending
            </span>
          </div>
          <p className="va-card-email">{admin.email}</p>
        </div>
      </div>
      <div className="va-card-details">
        <div className="va-detail-row">
          <span className="va-detail-label">Sent On:</span>
          <span className="va-detail-value">{formatDate(admin.created_on)}</span>
        </div>
        <div className="va-detail-row">
          <span className="va-detail-label">Status:</span>
          <span className="va-detail-value va-status-pending">Awaiting Registration</span>
        </div>
      </div>
      <div className="va-card-divider"></div>
    </div>
  );

  const renderRegistrationCard = (admin) => {
    const approvalStatus = getApprovalStatus(admin.approval);
    return (
      <div key={admin.id} className="va-admin-card va-card-registration">
        <div className="va-card-header">
          <div className="va-avatar" style={{ background: "#FFA500" }}>
            {admin.logo_url ? (
              <img src={admin.logo_url} alt={admin.name} className="va-avatar-img" />
            ) : (
              "🏢"
            )}
            <div className="va-pending-badge-avatar va-badge-new">1</div>
          </div>
          <div className="va-card-info">
            <div className="va-card-name-row">
              <h3 className="va-card-name">{admin.name || "N/A"}</h3>
              <span className="va-status-badge" style={{ background: approvalStatus.bg, color: approvalStatus.color }}>
                {approvalStatus.icon} {approvalStatus.text}
              </span>
            </div>
            <p className="va-card-email">{admin.email}</p>
            <div className="va-card-meta">
              <span className="va-meta-item">👤 {admin.contact_name || "N/A"}</span>
              <span className="va-meta-item">📱 {admin.mobile_no || "N/A"}</span>
            </div>
          </div>
        </div>
        <div className="va-card-details">
          <div className="va-detail-row">
            <span className="va-detail-label">Pilots:</span>
            <span className="va-detail-value">{admin.actual_pilots || 0}</span>
          </div>
          <div className="va-detail-row">
            <span className="va-detail-label">Drones:</span>
            <span className="va-detail-value">{admin.drones || 0}</span>
          </div>
          <div className="va-detail-row">
            <span className="va-detail-label">Registered:</span>
            <span className="va-detail-value">{formatDate(admin.created_on)}</span>
          </div>
        </div>
        <div className="va-card-divider"></div>
        <div className="va-action-buttons">
          {approvalStatus.canApprove && (
            <button className="va-btn-approve" onClick={() => openActionModal(admin, 'approve')} disabled={actionLoading}>
              ✓ Approve
            </button>
          )}
          {approvalStatus.canReject && (
            <button className="va-btn-reject" onClick={() => openActionModal(admin, 'reject')} disabled={actionLoading}>
              ✗ Reject
            </button>
          )}
          <button className="va-btn-view" onClick={() => handleViewDetails(admin)}>
            👁️ View
          </button>
        </div>
      </div>
    );
  };

  const renderApprovedCard = (admin) => {
    const approvalStatus = getApprovalStatus(admin.approval);
    return (
      <div key={admin.id} className="va-admin-card va-card-approved">
        <div className="va-card-header">
          <div className="va-avatar" style={{ background: "#00B894" }}>
            {admin.logo_url ? (
              <img src={admin.logo_url} alt={admin.name} className="va-avatar-img" />
            ) : (
              "🏢"
            )}
          </div>
          <div className="va-card-info">
            <div className="va-card-name-row">
              <h3 className="va-card-name">{admin.name || "N/A"}</h3>
              <span className="va-status-badge" style={{ background: approvalStatus.bg, color: approvalStatus.color }}>
                {approvalStatus.icon} {approvalStatus.text}
              </span>
            </div>
            <p className="va-card-email">{admin.email}</p>
            <div className="va-card-meta">
              <span className="va-meta-item">👤 {admin.contact_name || "No contact"}</span>
              <span className="va-meta-item">📱 {admin.mobile_no || "N/A"}</span>
            </div>
          </div>
        </div>
        <div className="va-card-details">
          <div className="va-detail-row">
            <span className="va-detail-label">Pilots:</span>
            <span className="va-detail-value">{admin.actual_pilots || 0}</span>
          </div>
          <div className="va-detail-row">
            <span className="va-detail-label">Drones:</span>
            <span className="va-detail-value">{admin.vehicles || 0}</span>
          </div>
          <div className="va-detail-row">
            <span className="va-detail-label">Approved On:</span>
            <span className="va-detail-value">
              {formatDate(admin.status_date || admin.created_on)}
            </span>
          </div>
          <div className="va-detail-row">
            <span className="va-detail-label">
              DGCA Company:
            </span>

            <span
              className="va-detail-value"
              style={{
                color: admin.is_dgca_company
                  ? "#00B894"
                  : "#95a5a6",
                fontWeight: "600"
              }}
            >
              {admin.is_dgca_company ? "Yes" : "No"}
            </span>
          </div>

          {/* {admin.is_dgca_company && (
            <>
              <div className="va-detail-row">
                <span className="va-detail-label">
                  Generated Keys:
                </span>

                <span className="va-detail-value">
                  {admin.generated_keys || 0}
                </span>
              </div>

              <div className="va-detail-row">
                <span className="va-detail-label">
                  Key Limit:
                </span>

                <span className="va-detail-value">
                  {admin.key_limit ?? 0}
                </span>
              </div>

              <div className="va-detail-row">
                <span className="va-detail-label">
                  Remaining Keys:
                </span>

                <span className="va-detail-value">
                  {Math.max(
                    0,
                    (admin.key_limit || 0) -
                    (admin.generated_keys || 0)
                  )}
                </span>
              </div>
            </>
          )} */}
          <div className="va-detail-row">
            <span className="va-detail-label">
              Superadmin's Company:
            </span>

            <span
              className="va-detail-value"
              style={{
                color: admin.is_superadmin_company
                  ? "#00B894"
                  : "#95a5a6",
                fontWeight: "600"
              }}
            >
              {admin.is_superadmin_company ? "Yes" : "No"}
            </span>
          </div>
          <div className="va-detail-row view-row">
            <button
              className="va-btn-view view-btn"
              onClick={() => handleViewDetails(admin)}
            >
              👁️ View Details
            </button>
          </div>

        </div>
        <div className="va-card-divider"></div>
        <div className="va-action-buttons va-button-separtion">

          {/* ROW 1 */}
          <div className="va-action-row">
            <button
              className="va-btn-superadmin"
              onClick={() => openActionModal(admin, "superadmin")}
              disabled={dgcaLoadingId === admin.id}
            >
              {dgcaLoadingId === admin.id
                ? "Processing..."
                : admin.is_superadmin_company
                  ? "✓ Superadmin Company"
                  : "🏢 Mark as Superadmin"}
            </button>

            {approvalStatus.canReject && (
              <button
                className="va-btn-reject"
                onClick={() => openActionModal(admin, "reject")}
                disabled={actionLoading}
              >
                ✗ Reject
              </button>
            )}
          </div>


          {/* ROW 2 */}
          <div className="va-action-row">

            <button
              className="va-btn-switch"
              onClick={() => openActionModal(admin, "switch")}
              disabled={switchLoading}
            >
              🔄 Switch to Dashboard
            </button>

            <div className="va-dgca-wrapper">

              <button
                className="va-btn-dgca"
                onClick={() => openActionModal(admin, "dgca")}
                disabled={dgcaLoadingId === admin.id}
              >
                {dgcaLoadingId === admin.id
                  ? "Processing..."
                  : admin.is_dgca_company
                    ? "✓ DGCA Company"
                    : "🏢 Mark as DGCA"}

                {admin.is_dgca_company && (
                  <span
                    className="va-dgca-edit-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditKeyLimit(admin);
                    }}
                    title="Edit Key Limit"
                  >
                    ✎
                  </span>
                )}
              </button>

            </div>

          </div>


          {/* KEY LIMIT SECTION */}
          {admin.is_dgca_company && showKeyLimitInput[admin.id] && (
            <div className="va-key-limit-section">

              <div className="va-key-limit-header">
                <span className="va-key-limit-title">
                  DGCA Key Limit
                </span>

                <span className="va-key-limit-current">
                  Current: {admin.key_limit ?? 0}
                </span>
              </div>

              <div className="va-key-limit-controls">

                <input
                  type="number"
                  min="0"
                  placeholder="Enter key limit"
                  value={keyLimitInputs[admin.id] ?? ""}
                  onChange={(e) => {
                    setKeyLimitInputs(prev => ({
                      ...prev,
                      [admin.id]: e.target.value
                    }));
                  }}
                  className="va-key-limit-input"
                />

                <button
                  className="va-btn-set-limit"
                  onClick={() => handleKeyLimitUpdate(admin)}
                  disabled={keyLimitLoadingId === admin.id}
                >
                  {keyLimitLoadingId === admin.id
                    ? "Saving..."
                    : "Set Limit"}
                </button>

              </div>

            </div>
          )}

        </div>
      </div>
    );
  };

  const renderRejectedCard = (admin) => {
    const approvalStatus = getApprovalStatus(admin.approval);
    return (
      <div key={admin.id} className="va-admin-card va-card-rejected">
        <div className="va-card-header">
          <div className="va-avatar" style={{ background: "#FF4757" }}>
            ❌
          </div>
          <div className="va-card-info">
            <div className="va-card-name-row">
              <h3 className="va-card-name">{admin.email}</h3>
              <span className="va-status-badge" style={{ background: approvalStatus.bg, color: approvalStatus.color }}>
                {approvalStatus.icon} {approvalStatus.text}
              </span>
            </div>
            {admin.name && <p className="va-card-email">{admin.name}</p>}
            <div className="va-card-meta">
              <span className="va-meta-item">📅 {formatDate(admin.created_on)}</span>
            </div>
          </div>
        </div>
        <div className="va-card-details">
          <div className="va-detail-row">
            <span className="va-detail-label">
              {admin.approval === 3 ? "Rejected On:" : "Status Updated On:"}
            </span>
            <span className="va-detail-value">
              {formatDate(admin.status_date || admin.created_on)}
            </span>
          </div>
        </div>
        <div className="va-card-divider"></div>
        <div className="va-action-buttons">
          {approvalStatus.canActivate && (
            <button
              className="va-btn-activate"
              onClick={() => openActionModal(admin, 'activate')}
              disabled={actionLoading}
            >
              🔄 Activate
            </button>
          )}

          <button
            className="va-btn-switch"
            onClick={() => openActionModal(admin, "switch")}
            disabled={switchLoading}
          >
            🔄 Switch to Dashboard
          </button>

          <button
            className="va-btn-view"
            onClick={() => handleViewDetails(admin)}
          >
            👁️ View Details
          </button>
        </div>
      </div>
    );
  };

  const renderGridView = () => {
    if (filteredData.length === 0) {
      return (
        <div className="va-no-results">
          <div className="va-no-results-icon">📭</div>
          <h3>No {filter.replace('-', ' ')} found</h3>
        </div>
      );
    }

    return (
      <div className="va-grid">
        {filteredData.map(admin => {
          if (filter === "pending-invitations") return renderInvitationCard(admin);
          if (filter === "pending-registrations") return renderRegistrationCard(admin);
          if (filter === "approved") return renderApprovedCard(admin);
          if (filter === "rejected") return renderRejectedCard(admin);
          return null;
        })}
      </div>
    );
  };

  const renderListView = () => {
    if (filteredData.length === 0) {
      return (
        <div className="va-no-results">
          <div className="va-no-results-icon">📭</div>
          <h3>No {filter.replace('-', ' ')} found</h3>
          <p>Try adjusting your search or filter criteria</p>
          <button className="va-reset-btn" onClick={() => { setFilter("pending-invitations"); setSearchTerm(""); }}>
            Reset Filters
          </button>
        </div>
      );
    }

    return (
      <div className="tr-table-container">
        <table className="tr-table">
          <thead className="tr-thead">
            <tr>
              <th>Admin</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Details</th>
              <th>
                {filter === "approved"
                  ? "Approved On"
                  : filter === "rejected"
                    ? "Rejected On"
                    : "Registered On"}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map(admin => {
              const approvalStatus = getApprovalStatus(admin.approval);
              return (
                <tr key={admin.id}>
                  <td>
                    <div className="va-table-admin">
                      <div className="va-table-avatar" style={{ background: approvalStatus.color }}>
                        {admin.logo_url ? (
                          <img src={admin.logo_url} alt={admin.name} className="va-table-avatar-img" />
                        ) : (
                          filter === "pending-invitations" ? "📧" : "🏢"
                        )}
                      </div>
                      <div className="va-table-admin-info">
                        <div className="va-table-name">{admin.name || admin.email}</div>
                        <div className="va-table-email">{admin.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="va-table-contact">
                      {admin.contact_name && <div>{admin.contact_name}</div>}
                      <div className="va-table-mobile">{admin.mobile_no || "N/A"}</div>
                    </div>
                  </td>
                  <td>
                    <span className="va-table-status" style={{ background: approvalStatus.bg, color: approvalStatus.color }}>
                      {approvalStatus.icon} {approvalStatus.text}
                    </span>
                  </td>
                  <td>
                    <div className="va-table-details">
                      <span>👥 Pilots: {admin.actual_pilots || 0}</span>
                      <span>🚁 Drones: {admin.vehicles || 0}</span>
                    </div>
                  </td>
                  <td>
                    <div className="va-table-date">
                      {filter === "approved" || filter === "rejected"
                        ? formatDate(admin.status_date || admin.created_on)
                        : formatDate(admin.created_on)}
                    </div>
                  </td>
                  <td>
                    <div className="va-table-actions">
                      {(filter === "pending-registrations") && (
                        <>
                          {approvalStatus.canApprove && (
                            <button onClick={() => openActionModal(admin, 'approve')} className="tr-table-btn tr-btn-approve" title="Approve">✓</button>
                          )}
                          {approvalStatus.canReject && (
                            <button onClick={() => openActionModal(admin, 'reject')} className="tr-table-btn tr-btn-reject" title="Reject">✗</button>
                          )}
                        </>
                      )}
                      {filter === "approved" && (
                        <>
                          {approvalStatus.canReject && (
                            <button
                              onClick={() => openActionModal(admin, 'reject')}
                              className="tr-table-btn tr-btn-reject"
                              title="Reject"
                            >
                              ✗
                            </button>
                          )}

                          <button
                            onClick={() => handleSwitchToAdmin(admin)}
                            className="tr-table-btn tr-btn-switch"
                            title="Switch to Dashboard"
                            disabled={switchLoading}
                          >
                            🔄
                          </button>
                        </>
                      )}
                      {filter === "rejected" && (
                        <>
                          {approvalStatus.canActivate && (
                            <button
                              onClick={() => openActionModal(admin, 'activate')}
                              className="va-table-btn va-btn-activate"
                              title="Activate"
                            >
                              🔄
                            </button>
                          )}

                          <button
                            onClick={() => handleSwitchToAdmin(admin)}
                            className="va-table-btn va-btn-switch"
                            title="Switch to Dashboard"
                            disabled={switchLoading}
                          >
                            🔄
                          </button>
                        </>
                      )}
                      <button onClick={() => handleViewDetails(admin)} className="tr-table-btn tr-btn-view" title="View">👁️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const getActionModalContent = () => {
    if (!selectedAdmin || !actionType) return null;

    switch (actionType) {
      case "approve":
        return {
          title: "Approve Admin",
          icon: "✓",
          iconClass: "va-confirm-approve",
          buttonClass: "va-confirm-approve-btn",
          message:
            "The admin will be able to access the platform after approval.",
          buttonText: "Yes, Approve",
        };

      case "reject":
        return {
          title: "Reject Admin",
          icon: "✕",
          iconClass: "va-confirm-reject",
          buttonClass: "va-confirm-reject-btn",
          message:
            "The admin account will be rejected and access will be denied.",
          buttonText: "Yes, Reject",
        };

      case "activate":
        return {
          title: "Activate Admin",
          icon: "↻",
          iconClass: "va-confirm-activate",
          buttonClass: "va-confirm-activate-btn",
          message:
            "The admin account will be activated. They will be able to access the platform again.",
          buttonText: "Yes, Activate",
        };

      case "dgca":
  return {
    title: selectedAdmin.is_dgca_company
      ? "Confirm Removal of DGCA Designation"
      : "Confirm DGCA Company Designation",
    icon: "🏢",
    iconClass: "va-confirm-dgca",
    buttonClass: "va-confirm-dgca-btn",
    message: selectedAdmin.is_dgca_company
      ? "This will remove the DGCA designation from this organization."
      : "This will designate this organization as a DGCA company.",
    buttonText: selectedAdmin.is_dgca_company
      ? "Yes, Remove Designation"
      : "Yes, Designate as DGCA",
  };

case "superadmin":
  return {
    title: selectedAdmin.is_superadmin_company
      ? "Confirm Removal of Own Company Designation"
      : "Confirm Own Company Designation",

    icon: "🏢",
    iconClass: "va-confirm-superadmin",
    buttonClass: "va-confirm-superadmin-btn",

    message: selectedAdmin.is_superadmin_company
      ? "This will remove the own company designation from this organization. The organization will remain associated with the Superadmin."
      : "This will designate this organization as the Superadmin's own company. Other organizations associated with the Superadmin will remain unchanged.",

    buttonText: selectedAdmin.is_superadmin_company
      ? "Yes, Remove Designation"
      : "Yes, Designate as Own Company",
  };
      case "switch":
        return {
          title: "Switch to Admin Dashboard",
          icon: "↗",
          iconClass: "va-confirm-switch",
          buttonClass: "va-confirm-approve-btn",
          message:
            "You will be redirected to this administrator's dashboard.",
          buttonText: "Yes, Switch to Dashboard",
        };

      default:
        return null;
    }
  };

  const isViewingAsAdmin = localStorage.getItem('view_as_admin_id');

  return (
    <div className="dashboard-wrapper">
      <Sidebar />
      <PopupMessage
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
        type={popupType}
      />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">View Admins</h1>
            <p className="dashboard-subtitle">Manage administrator invitations and registrations</p>
          </div>
          {isViewingAsAdmin && (
            <div className="header-right">
              <button
                className="va-btn-exit-view"
                onClick={handleExitAdminView}
                disabled={switchLoading}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                🔄 Exit Admin View
              </button>
            </div>
          )}
        </div>

        <div className="sa-filter-section">
          <div className="sa-search-wrapper">
            <FiSearch className="sa-search-icon" />
            <input
              type="text"
              placeholder="Search by email, name or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="sa-search-input"
            />
            <button
              className="sa-mobile-filter-toggle"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <FiFilter /> Filters
            </button>
          </div>

          <div className={`sa-filters ${showMobileFilters ? 'mobile-open' : ''}`}>
            <button
              className={`sa-filter-btn ${filter === "pending-invitations" ? "active" : ""}`}
              onClick={() => {
                setFilter("pending-invitations");
                setShowMobileFilters(false);
              }}
            >
              📧 Pending Invitations ({statusCounts["pending-invitations"]})
            </button>
            <button
              className={`sa-filter-btn ${filter === "pending-registrations" ? "active" : ""}`}
              onClick={() => {
                setFilter("pending-registrations");
                setShowMobileFilters(false);
              }}
            >
              📝 Pending Registrations ({statusCounts["pending-registrations"]})
            </button>
            <button
              className={`sa-filter-btn ${filter === "approved" ? "active" : ""}`}
              onClick={() => {
                setFilter("approved");
                setShowMobileFilters(false);
              }}
            >
              ✓ Active ({statusCounts["approved"]})
            </button>
            <button
              className={`sa-filter-btn ${filter === "rejected" ? "active" : ""}`}
              onClick={() => {
                setFilter("rejected");
                setShowMobileFilters(false);
              }}
            >
              ✗ Rejected ({statusCounts["rejected"]})
            </button>
          </div>
        </div>

        <div className="va-results-info">
          <span className="va-results-count">Showing {filteredData.length} items</span>
          {searchTerm && (
            <span className="va-search-term">
              Search: "{searchTerm}" <button onClick={() => setSearchTerm("")}>Clear</button>
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="va-loading-container">
            <img src={pavamanLogo} alt="Loading" className="va-spinning-logo" />
            <p className="va-loading-text">Loading administrators...</p>
          </div>
        ) : (
          viewMode === "grid" ? renderGridView() : renderListView()
        )}
      </div>

      {showDetailsModal && selectedAdmin && (
        <div className="va-modal-overlay" onClick={closeModal}>
          <div className="va-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <h2>Admin Details</h2>
              <button className="va-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="va-modal-body">
              <div className="va-details-grid">

<div className="va-detail-section">
  <h3>Basic Information</h3>

  {/* 4 fields → 2 + 2 */}
  <div className="va-info-grid">

    <div className="va-detail-item">
      <span className="va-detail-label">Organization:</span>
      <span className="va-detail-value">
        {selectedAdmin.name || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">Contact Person:</span>
      <span className="va-detail-value">
        {selectedAdmin.contact_name || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">Email:</span>
      <span className="va-detail-value">
        {selectedAdmin.email || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">Mobile:</span>
      <span className="va-detail-value">
        {selectedAdmin.mobile_no || "N/A"}
      </span>
    </div>

  </div>


  <h3 className="va-subsection-title">
    Address Information
  </h3>

  {/* 7 fields → 4 + 3 */}
  <div className="va-address-grid">

    <div className="va-detail-item">
      <span className="va-detail-label">Address:</span>
      <span className="va-detail-value">
        {selectedAdmin.address || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">District:</span>
      <span className="va-detail-value">
        {selectedAdmin.district || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">Mandal:</span>
      <span className="va-detail-value">
        {selectedAdmin.mandal || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">City:</span>
      <span className="va-detail-value">
        {selectedAdmin.city || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">State:</span>
      <span className="va-detail-value">
        {selectedAdmin.state || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">Landmark:</span>
      <span className="va-detail-value">
        {selectedAdmin.landmark || "N/A"}
      </span>
    </div>

    <div className="va-detail-item">
      <span className="va-detail-label">Pincode:</span>
      <span className="va-detail-value">
        {selectedAdmin.pincode || "N/A"}
      </span>
    </div>

  </div>
</div>


<div className="va-detail-section">
  <h3>Account Details</h3>

  <div className="va-account-grid">

    {/* 1 */}
    <div className="va-detail-item">
      <span className="va-detail-label">Status:</span>
      <span className="va-detail-value">
        {getApprovalStatus(selectedAdmin.approval).text}
      </span>
    </div>

    {/* 2 */}
    <div className="va-detail-item">
      <span className="va-detail-label">DGCA Company:</span>
      <span
        className="va-detail-value"
        style={{
          color: selectedAdmin.is_dgca_company
            ? "#00B894"
            : "#95a5a6",
          fontWeight: "600"
        }}
      >
        {selectedAdmin.is_dgca_company ? "Yes" : "No"}
      </span>
    </div>

    {/* 3 */}
    {selectedAdmin.is_dgca_company && (
      <div className="va-detail-item">
        <span className="va-detail-label">Generated Keys:</span>
        <span className="va-detail-value">
          {selectedAdmin.generated_keys || 0}
        </span>
      </div>
    )}

    {/* 4 */}
    {selectedAdmin.is_dgca_company && (
      <div className="va-detail-item">
        <span className="va-detail-label">Key Limit:</span>
        <span className="va-detail-value">
          {selectedAdmin.key_limit ?? 0}
        </span>
      </div>
    )}

    {/* 5 */}
    {selectedAdmin.is_dgca_company && (
      <div className="va-detail-item">
        <span className="va-detail-label">Remaining Keys:</span>
        <span className="va-detail-value">
          {Math.max(
            0,
            (selectedAdmin.key_limit || 0) -
            (selectedAdmin.generated_keys || 0)
          )}
        </span>
      </div>
    )}

    {/* 6 */}
    <div className="va-detail-item">
      <span className="va-detail-label">Registered On:</span>
      <span className="va-detail-value">
        {formatDate(selectedAdmin.created_on)}
      </span>
    </div>

    {/* 7 */}
    {selectedAdmin.status_date && (
      <div className="va-detail-item">
        <span className="va-detail-label">
          {selectedAdmin.approval === 2
            ? "Approved On:"
            : selectedAdmin.approval === 3
              ? "Rejected On:"
              : "Status Updated On:"}
        </span>

        <span className="va-detail-value">
          {formatDate(selectedAdmin.status_date)}
        </span>
      </div>
    )}

    {/* 8 */}
    {selectedAdmin.actual_pilots !== undefined && (
      <div className="va-detail-item">
        <span className="va-detail-label">Pilots:</span>
        <span className="va-detail-value">
          {selectedAdmin.actual_pilots || 0}
        </span>
      </div>
    )}

    {/* 9 */}
    {selectedAdmin.actual_pilots !== undefined && (
      <div className="va-detail-item">
        <span className="va-detail-label">Drones:</span>
        <span className="va-detail-value">
          {selectedAdmin.vehicles || 0}
        </span>
      </div>
    )}

  </div>
</div>
                {selectedAdmin.logo_url && (
                  <div className="va-detail-section">
                    <h3>Organization Logo</h3>
                    <img src={selectedAdmin.logo_url} alt="Logo" className="va-modal-logo" />
                  </div>
                )}
                {selectedAdmin.gst_url && (
                  <div className="va-detail-section">
                    <h3>GST Certificate</h3>
                    <a href={selectedAdmin.gst_url} target="_blank" rel="noopener noreferrer" className="va-gst-link">
                      View GST Certificate 📄
                    </a>
                  </div>
                )}
                {(selectedAdmin.approval === 2 || selectedAdmin.approval === 3) && (
                  <div className="va-detail-section">
                    <h3>Quick Actions</h3>
                    <button
                      className="va-btn-switch-full"
                      onClick={() => {
                        closeModal();
                        handleSwitchToAdmin(selectedAdmin);
                      }}
                      disabled={switchLoading}
                    >
                      🔄 Switch to {selectedAdmin.name || selectedAdmin.email} Dashboard
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="va-modal-footer">
              <button className="va-modal-close-btn" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}


      {showActionModal && selectedAdmin && actionType && (
        <div className="va-modal-overlay" onClick={closeActionModal}>
          <div className="va-modal-content va-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="va-modal-header">
              <h2>{getActionModalContent().title}</h2>
              <button className="va-modal-close" onClick={closeActionModal}>✕</button>
            </div>
            <div className="va-modal-body">
              <div className="va-confirm-container">
                <div className={`va-confirm-icon ${getActionModalContent().iconClass}`}>
                  {getActionModalContent().icon}
                </div>
               <p className="va-confirm-text">
  Are you sure you want to{" "}
  {actionType === "dgca"
    ? selectedAdmin.is_dgca_company
      ? "remove the DGCA designation from"
      : "designate"
    : actionType === "superadmin"
      ? selectedAdmin.is_superadmin_company
        ? "remove the Superadmin designation from"
        : "designate"
      : actionType}{" "}
  <strong>{selectedAdmin.name || selectedAdmin.email}</strong>
  {actionType === "dgca" && !selectedAdmin.is_dgca_company
    ? " as a DGCA company?"
    : actionType === "superadmin" && !selectedAdmin.is_superadmin_company
      ? " as a Superadmin's own company?"
      : "?"}
</p>
                <p className="va-confirm-subtext">
                  {getActionModalContent().message}
                </p>
                <div className="va-confirm-buttons">
                  <button className="va-confirm-cancel" onClick={closeActionModal} disabled={actionLoading}>
                    Cancel
                  </button>
                  <button
                    className={getActionModalContent().buttonClass}
                    onClick={handleConfirmedAction}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <>
                        <span className="va-spinner-small"></span>
                        Processing...
                      </>
                    ) : (
                      getActionModalContent().buttonText
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAllAdmins;